import { Check, AlertCircle, Minus, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

const AVAILABILITY_TRANSLATIONS = {
  sv: {
    "Available": "Tillgängligt",
    "Limited": "Begränsat",
    "Very Limited": "Mycket begränsat",
    "2 remaining": "2 kvar",
    "3 remaining": "3 kvar",
    "Sold Out": "Slutsålt",
    "Coming Soon": "Kommer snart",
  },
  en: {},
};

export default function PackageCard({ pkg, index = 0, quantity, onQuantityChange, onSelect }) {
  const { t, lang } = useLang();
  const isHighlight = pkg.highlight;
  const isLimited = pkg.availability !== "Available";
  const availabilityLabel = AVAILABILITY_TRANSLATIONS[lang]?.[pkg.availability] || pkg.availability;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`package-card-${index}`}
      className={`package-card relative border p-8 ${
        isHighlight
          ? "highlight border-[#C5A059]/30 bg-gradient-to-br from-[#C5A059]/5 to-[#141414]"
          : "border-[#262626] bg-[#141414]"
      }`}
    >
      {isHighlight && (
        <div className="absolute -top-px left-8 px-4 py-1 bg-[#C5A059] text-black text-[10px] tracking-[0.2em] uppercase font-['Outfit'] font-bold">
          {t("recommended")}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
        <div className="flex-1">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-1">{pkg.name}</h3>
          {pkg.guests && <p className="text-white/50 text-sm font-['Outfit'] font-light mb-4">{pkg.guests}</p>}
          <div className="space-y-2.5 mb-6">
            {pkg.items.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                <span className="text-white/60 text-sm font-['Outfit'] font-light">{item}</span>
              </div>
            ))}
          </div>
          {isLimited && (
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={14} className="text-amber-500" />
              <span className="text-amber-500 text-xs font-['Outfit'] font-medium">{availabilityLabel}</span>
            </div>
          )}
        </div>

        <div className="md:text-right flex flex-col items-start md:items-end gap-4">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-['Outfit'] font-semibold text-white">{pkg.price.toLocaleString()}</span>
              <span className="text-sm text-white/50 font-['Outfit']">{pkg.currency}</span>
            </div>
            <p className="text-xs text-white/40 font-['Outfit']">{pkg.price_label}</p>
          </div>

          {/* Quantity selector */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50 font-['Outfit'] uppercase tracking-wider">{t("qty")}</span>
            <div className="flex items-center border border-[#262626]">
              <button onClick={() => onQuantityChange(Math.max(1, quantity - 1))} data-testid={`qty-minus-${index}`}
                className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/5 transition-colors"><Minus size={14} /></button>
              <span className="px-4 py-2 text-white font-['Outfit'] font-medium text-sm min-w-[40px] text-center border-x border-[#262626]" data-testid={`qty-value-${index}`}>{quantity}</span>
              <button onClick={() => onQuantityChange(quantity + 1)} data-testid={`qty-plus-${index}`}
                className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/5 transition-colors"><Plus size={14} /></button>
            </div>
          </div>

          {quantity > 1 && (
            <p className="text-xs text-[#C5A059] font-['Outfit']">{t("total")}: {(pkg.price * quantity).toLocaleString()} {pkg.currency}</p>
          )}

          <div className="flex flex-col gap-2 w-full md:w-auto">
            <button onClick={() => onSelect?.(pkg, "card")} data-testid={`package-buy-btn-${index}`}
              className="btn-primary px-8 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] whitespace-nowrap">
              {t("pay_card")}
            </button>
            <button onClick={() => onSelect?.(pkg, "invoice")} data-testid={`package-invoice-btn-${index}`}
              className="btn-secondary px-8 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] whitespace-nowrap">
              {t("request_invoice")}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
